import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaRegSave } from "react-icons/fa";
import Store from "../../assets/images/store_mall_directory.png";
import visa from "../../assets/images/Visa.png";
import Mastercard from "../../assets/images/Mastercard.png";
import AmericanExpress from "../../assets/images/AmericanExpress.png";
import Unionpay from "../../assets/images/unionpay.png";
import payattitude from "../../assets/images/Payattitude.png";
import verve from "../../assets/images/verve.png";
import afrigo from "../../assets/images/afrigo-logo.png";
import {
  deletePaymentMethod,
  deletePaymentScheme,
  fetchMethodMerchant,
  fetchPaymentMethod,
  fetchPaymentScheme,
  fetchSchemeMerchant,
  getSingleMerchant,
  getTxpgInfo,
  initiatePaymentMethod,
  initiatePaymentScheme,
  migrateSingleMerchant,
  submitFeeConfiguration,
} from "../../services/merchant";
import toast from "react-hot-toast";
import Pagination from "../../components/Pagination";
import Spinner from "../../components/Spinner";
import { useAuth } from "../../context/AuthContextProvider";
import EditMerchant from "./EditMerchant";
import EditTxpg from "./EditTxpg";
import { useQuery } from "@tanstack/react-query";

const MerchantInfo = () => {
  const [merchantDetails, setMerchantDetails] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [editTxpgModal, setEditTxpgModal] = useState(false);
  const [hasTxpg, setHasTxpg] = useState(false);
  const [txpgDetails, setTxpgDetails] = useState({});

  const [showMscModal, setShowMscModal] = useState(false);
  const [mscStep, setMscStep] = useState(1);
  const [payer, setPayer] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("NGN");
  const [scheme, setScheme] = useState("card");
  const [feeType, setFeeType] = useState("Percentage");
  const [feeValue, setFeeValue] = useState("");
  const [maxFeeCap, setMaxFeeCap] = useState("");
  const [noCap, setNoCap] = useState(true);
  const [submittingMsc, setSubmittingMsc] = useState(false);
  const [mscSuccessMsg, setMscSuccessMsg] = useState("");
  const [mscErrorMsg, setMscErrorMsg] = useState("");

  const resetMscState = () => {
    setMscStep(1);
    setPayer(null);
    setCurrencyCode("NGN");
    setScheme("card");
    setFeeType("Percentage");
    setFeeValue("");
    setMaxFeeCap("");
    setNoCap(true);
    setSubmittingMsc(false);
    setMscSuccessMsg("");
    setMscErrorMsg("");
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const { userrole } = useAuth();

  const fetchTxpgInfo = async () => {
    try {
      const res = await getTxpgInfo(id);
      setHasTxpg(true);
      setTxpgDetails(res.data);
      return res;
    } catch (error) {
      if (error.response) {
        setHasTxpg(false);
        toast.error(error.response.data.message);
      }
      return { data: null };
    }
  };

  const getPaymentSchemes = async () => {
    try {
      const res = await fetchPaymentScheme();
      setSchemes(res.data);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const getPaymentMethods = async () => {
    try {
      const res = await fetchPaymentMethod();
      setMethods(res.data);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const postPaymentMethods = async () => {
    if (userrole?.role === "Risk User") {
      toast.error("You are not authorized to perform this action.");
      return;
    }
    try {
      const res = await initiatePaymentMethod({
        merchantId: id,
        data: selectedMethods,
      });
      toast.success(res.message);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const postPaymentScheme = async () => {
    if (!selectedMethods.includes("pay with card")) return;
    if (userrole?.role === "Risk User") {
      toast.error("You are not authorized to perform this action.");
      return;
    }
    try {
      const res = await initiatePaymentScheme({
        merchantId: id,
        data: selectedSchemes,
      });
      toast.success(res.message);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const getMerchantMethod = useCallback(async () => {
    try {
      const res = await fetchMethodMerchant(id);
      const methodNames = res.data.map((m) => m.paymentMethod); // use .code or .id if that’s the identifier
      setSelectedMethods(methodNames);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  }, [id]);

  const getMerchantScheme = useCallback(async () => {
    try {
      const res = await fetchSchemeMerchant(id);
      const schemeNames = res.data.map((s) => s.scheme);
      setSelectedSchemes(schemeNames);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  }, [id]);

  const deleteScheme = async () => {
    if (userrole?.role === "Risk User") {
      toast.error("You are not authorized to perform this action.");
      return;
    }
    try {
      const res = await deletePaymentScheme({
        merchantId: id,
        data: selectedSchemes,
      });
      toast.success(res.message);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const deleteMethod = async () => {
    if (userrole?.role === "Risk User") {
      toast.error("You are not authorized to perform this action.");
      return;
    }
    try {
      const res = await deletePaymentMethod({
        merchantId: id,
        data: selectedMethods,
      });
      toast.success(res.message);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const query = useQuery({
    queryKey: ["fetchTxpgDetails"],
    queryFn: fetchTxpgInfo,
  });

  console.log(query?.data);

  useEffect(() => {
    getPaymentSchemes();
  }, []);

  useEffect(() => {
    getPaymentMethods();
  }, []);

  useEffect(() => {
    getMerchantMethod();
  }, [getMerchantMethod]);

  useEffect(() => {
    getMerchantScheme();
  }, [getMerchantScheme]);

  const schemeImages = [
    {
      name: "visa",
      image: visa,
    },
    {
      name: "master card",
      image: Mastercard,
    },
    {
      name: "american express",
      image: AmericanExpress,
    },
    {
      name: "union pay",
      image: Unionpay,
    },
    {
      name: "payattitudecard",
      image: payattitude,
    },
    {
      name: "verve",
      image: verve,
    },
    {
      name: "afrigo",
      image: afrigo,
    },
  ];

  const transactions = [
    {
      transactionId: "#CM9801",
      transactionDesc: "UAJ Microfinance",
      paymentRRN: "a@uaj.ng",
      maskedPan: "5673********8900",
      amount: "NGN3,465,676",
      transactionDate: "Just now",
    },
    {
      transactionId: "#CM9801",
      transactionDesc: "UAJ Microfinance",
      paymentRRN: "a@uaj.ng",
      maskedPan: "5673********8900",
      amount: "NGN3,465,676",
      transactionDate: "Just now",
    },
    {
      transactionId: "#CM9801",
      transactionDesc: "UAJ Microfinance",
      paymentRRN: "a@uaj.ng",
      maskedPan: "5673********8900",
      amount: "NGN3,465,676",
      transactionDate: "Just now",
    },
    {
      transactionId: "#CM9801",
      transactionDesc: "UAJ Microfinance",
      paymentRRN: "a@uaj.ng",
      maskedPan: "5673********8900",
      amount: "NGN3,465,676",
      transactionDate: "Just now",
    },
    {
      transactionId: "#CM9801",
      transactionDesc: "UAJ Microfinance",
      paymentRRN: "a@uaj.ng",
      maskedPan: "5673********8900",
      amount: "NGN3,465,676",
      transactionDate: "Just now",
    },
  ];

  const getMerchant = useCallback(async () => {
    try {
      const res = await getSingleMerchant(id);
      setMerchantDetails(res.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [id]);

  const handleMigrate = async () => {
    setLoading(true);
    try {
      const res = await migrateSingleMerchant(id);
      toast.success(res.message);
      setLoading(false);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    getMerchant();
  }, [getMerchant]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = merchantDetails?.merchantTransactions?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleMscSubmit = async (e) => {
    if (e) e.preventDefault();

    if (payer === "customer") {
      if (!feeValue || isNaN(feeValue) || Number(feeValue) < 0) {
        toast.error("Please enter a valid fee value.");
        return;
      }
    }

    setSubmittingMsc(true);
    setMscSuccessMsg("");
    setMscErrorMsg("");

    try {
      const payload = {
        merchantId: Number(id),
        currencyCode: payer === "customer" ? currencyCode : "NGN",
        scheme: payer === "customer" ? scheme : "card",
        feeType: payer === "customer" ? feeType : "Percentage",
        feeValue: payer === "customer" ? Number(feeValue) : 0,
        isCustomerPaying: payer === "customer"
      };

      if (payer === "customer" && !noCap && maxFeeCap !== "") {
        payload.maxFeeCap = Number(maxFeeCap);
      }

      const res = await submitFeeConfiguration(payload);

      setMscSuccessMsg(res.message || "Fee configuration submitted successfully!");
      toast.success(res.message || "Fee configuration submitted successfully!");
      setTimeout(() => {
        setShowMscModal(false);
        resetMscState();
      }, 2000);
    } catch (error) {
      console.error("Error configuring MSC:", error);
      const errMsg = error?.response?.data?.message || "Failed to submit fee configuration. Please check your inputs.";
      setMscErrorMsg(errMsg);
      toast.error(errMsg);
    } finally {
      setSubmittingMsc(false);
    }
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="h-screen">
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span>
            <svg
              onClick={() => navigate(-1)}
              className="cursor-pointer"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 11H6.82998L9.70998 8.11998C10.1 7.72998 10.1 7.09998 9.70998 6.70998C9.31998 6.31998 8.68998 6.31998 8.29998 6.70998L3.70998 11.3C3.31998 11.69 3.31998 12.32 3.70998 12.71L8.29998 17.3C8.68998 17.69 9.31998 17.69 9.70998 17.3C10.1 16.91 10.1 16.28 9.70998 15.89L6.82998 13H20C20.55 13 21 12.55 21 12C21 11.45 20.55 11 20 11Z"
                fill="#323232"
              />
            </svg>
          </span>
          <h1 className="font-bold text-2xl text-navy-blue">
            Merchant Profile
          </h1>
        </div>
        <div className="w-[56px] h-[42px] rounded-[10px] bg-text-orange relative">
          <span>
            <svg
              className="absolute left-4 top-2"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_3314_7311)">
                <path
                  d="M15.5006 14.0006H14.7106L14.4306 13.7306C15.6306 12.3306 16.2506 10.4206 15.9106 8.39063C15.4406 5.61063 13.1206 3.39063 10.3206 3.05063C6.09063 2.53063 2.53063 6.09063 3.05063 10.3206C3.39063 13.1206 5.61063 15.4406 8.39063 15.9106C10.4206 16.2506 12.3306 15.6306 13.7306 14.4306L14.0006 14.7106V15.5006L18.2506 19.7506C18.6606 20.1606 19.3306 20.1606 19.7406 19.7506C20.1506 19.3406 20.1506 18.6706 19.7406 18.2606L15.5006 14.0006ZM9.50063 14.0006C7.01063 14.0006 5.00063 11.9906 5.00063 9.50063C5.00063 7.01063 7.01063 5.00063 9.50063 5.00063C11.9906 5.00063 14.0006 7.01063 14.0006 9.50063C14.0006 11.9906 11.9906 14.0006 9.50063 14.0006Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_3314_7311">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
        </div>
      </div>
      <div className="flex justify-end mt-6 gap-4">
        <button
          className="w-[280px] h-[50px] bg-blue-500 hover:bg-blue-600 text-sm text-white rounded-md cursor-pointer"
          type="button"
          onClick={() => {
            resetMscState();
            setShowMscModal(true);
          }}
        >
          ⚙️ Configure MSC
        </button>
        <button
          className="w-[280px] h-[50px] bg-black hover:bg-gray-800 text-sm text-white rounded-md cursor-pointer"
          type="button"
          onClick={handleMigrate}
        >
          {loading ? <Spinner /> : "Migrate to Payloft"}
        </button>
      </div>
      <div className="flex mt-10 gap-2 w-full">
        <div className="flex flex-col gap-2">
          <div className="bg-white rounded-md shadow-md w-full h-[601px] pt-[60px] px-[20px] pb-[10px]">
            <div className="flex flex-col items-center">
              <img src={Store} alt="store" className="w-[120px] h-[120px]" />
              <p className="text-lg font-bold text-blue-grey/[87%]">
                {merchantDetails?.name}
              </p>
              <p className="text-black-shade text-[12px] font-normal">
                Merchant ID: #{id}
              </p>
            </div>
            <div>
              <p className="border-b mt-4 text-sm font-bold text-blue-grey/[87%] pb-1">
                Details
              </p>
              <div className="flex items-center mt-1 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Phone Number:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%]">
                  {merchantDetails?.phoneNumber}
                </span>
              </div>
              <div className="flex flex-wrap items-center mt-2 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Email:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%] break-words">
                  {merchantDetails?.email}
                </span>
              </div>
              <div className="flex items-center mt-2 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Status:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%]">
                  {merchantDetails?.kycStatus}
                </span>
              </div>
              <div className="flex items-center mt-2 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Bank:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%]">
                  {merchantDetails?.bank}
                </span>
              </div>

              <div className="flex items-center mt-2 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Account Number:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%]">
                  {merchantDetails?.account}
                </span>
              </div>
              <div className="flex items-center mt-2 gap-1">
                <span className="text-blue-grey/[87%] text-[15px] font-medium">
                  Country:
                </span>
                <span className="text-[15px] font-medium text-blue-grey/[68%]">
                  {merchantDetails?.country}
                </span>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <button
                className="bg-primary text-white w-[80px] h-[42px] rounded-md shadow-md"
                onClick={() => setEditModal(true)}
              >
                Edit
              </button>
            </div>
          </div>
          <div className="bg-white rounded-md shadow-md w-full pt-[60px] px-[20px] pb-[10px]">
            {query?.data?.data ? (
              <>
                <div className="flex items-center mt-2 gap-1">
                  <span className="text-blue-grey/[87%] text-[15px] font-medium">
                    Username:
                  </span>
                  <span className="text-[15px] font-medium text-blue-grey/[68%] break-words">
                    {query?.data?.data?.username}
                  </span>
                </div>
                <div className="flex items-center mt-2 gap-1">
                  <span className="text-blue-grey/[87%] text-[15px] font-medium">
                    Password:
                  </span>
                  <span className="text-[15px] font-medium text-blue-grey/[68%]">
                    {query?.data?.data?.password}
                  </span>
                </div>
                <div className="flex items-center mt-2 gap-1">
                  <span className="text-blue-grey/[87%] text-[15px] font-medium">
                    Terminal Id:
                  </span>
                  <span className="text-[15px] font-medium text-blue-grey/[68%]">
                    {query?.data?.data?.terminalId}
                  </span>
                </div>
                <div className="flex items-center mt-2 gap-1">
                  <span className="text-blue-grey/[87%] text-[15px] font-medium">
                    Party Id:
                  </span>
                  <span className="text-[15px] font-medium text-blue-grey/[68%]">
                    {query?.data?.data?.partyId}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500">
                No Txpg details found.
              </p>
            )}
            <div className="flex justify-center mt-6">
              <button
                className="bg-primary text-white w-[100px] h-[42px] rounded-md shadow-md"
                onClick={() => setEditTxpgModal(true)}
              >
                {hasTxpg ? "Edit Txpg" : "Create Txpg"}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col w-3/4">
          <div className="flex items-center gap-2">
            <div className=" bg-white rounded-md w-1/2 h-full shadow-md">
              <p className="text-xl font-medium text-blue-grey/[87%] p-5">
                Setup Payment Method
              </p>
              <div className="flex items-center justify-between border-2 py-4">
                <div className="flex items-center w-[50%] px-3 justify-between">
                  <span className="text-[12px] font-semibold text-blue-grey/[87%]">
                    Payment Method
                  </span>
                </div>
                <span className=" border-x-2 px-3 text-[12px] font-normal text-black-shade">
                  {selectedMethods.length} Selected
                </span>
                <div className="flex items-center px-3">
                  <span className="w-[30%] flex items-center gap-6 gap justify-between">
                    <span>
                      <svg
                        width="16"
                        height="15"
                        viewBox="0 0 16 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.5 12.0504V14.5834C0.5 14.8167 0.683308 15 0.916609 15H3.44959C3.55791 15 3.66623 14.9583 3.74122 14.875L12.84 5.78461L9.71539 2.66005L0.624983 11.7505C0.541661 11.8338 0.5 11.9338 0.5 12.0504ZM15.2563 3.36828C15.5812 3.04333 15.5812 2.5184 15.2563 2.19345L13.3066 0.243716C12.9816 -0.0812387 12.4567 -0.0812387 12.1317 0.243716L10.6069 1.7685L13.7315 4.89307L15.2563 3.36828Z"
                          fill="#1C1C1C"
                        />
                      </svg>
                    </span>
                    <span>
                      <svg
                        onClick={() => deleteMethod()}
                        className="cursor-pointer"
                        width="15"
                        height="17"
                        viewBox="0 0 15 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.375 2.75H0.625C0.279822 2.75 0 3.02982 0 3.375C0 3.72018 0.279822 4 0.625 4H14.375C14.7202 4 15 3.72018 15 3.375C15 3.02982 14.7202 2.75 14.375 2.75Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M5 7.125V12.125C5 12.4702 5.27982 12.75 5.625 12.75C5.97018 12.75 6.25 12.4702 6.25 12.125V7.125C6.25 6.77982 5.97018 6.5 5.625 6.5C5.27982 6.5 5 6.77982 5 7.125Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M8.75 7.125V12.125C8.75 12.4702 9.02982 12.75 9.375 12.75C9.72018 12.75 10 12.4702 10 12.125V7.125C10 6.77982 9.72018 6.5 9.375 6.5C9.02982 6.5 8.75 6.77982 8.75 7.125Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M2.5 15.25V3.375C2.5 3.02982 2.22018 2.75 1.875 2.75C1.52982 2.75 1.25 3.02982 1.25 3.375V15.25C1.25 15.7678 1.61612 16.1339 1.61612 16.1339C1.98223 16.5 2.5 16.5 2.5 16.5H12.5C13.0178 16.5 13.3839 16.1339 13.3839 16.1339C13.75 15.7678 13.75 15.25 13.75 15.25V3.375C13.75 3.02982 13.4702 2.75 13.125 2.75C12.7798 2.75 12.5 3.02982 12.5 3.375V15.25H2.5Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M4.29918 0.799175C3.75 1.34835 3.75 2.125 3.75 2.125V3.375C3.75 3.72018 4.02982 4 4.375 4C4.72018 4 5 3.72018 5 3.375V2.125C5 1.86612 5.18306 1.68306 5.18306 1.68306C5.36612 1.5 5.625 1.5 5.625 1.5H9.375C9.63388 1.5 9.81694 1.68306 9.81694 1.68306C10 1.86612 10 2.125 10 2.125V3.375C10 3.72018 10.2798 4 10.625 4C10.9702 4 11.25 3.72018 11.25 3.375V2.125C11.25 1.34835 10.7008 0.799175 10.7008 0.799175C10.1517 0.25 9.375 0.25 9.375 0.25H5.625C4.84835 0.25 4.29918 0.799175 4.29918 0.799175Z"
                          fill="#1C1C1C"
                        />
                      </svg>
                    </span>
                    <span>
                      <FaRegSave
                        className="cursor-pointer"
                        onClick={() => postPaymentMethods()}
                      />
                    </span>
                  </span>
                </div>
              </div>
              <table className="w-[100%]">
                <thead>
                  <tr className="text-left border-b-2">
                    {/* <th className="text-[12px] font-normal text-black-shade/40 border-r p-3">
                      <div className="">
                        <label
                          class="container"
                          style={{
                            fontWeight: "normal",
                          }}
                        >
                          Logo
                          <input type="checkbox" />
                          <span class="checkmark"></span>
                        </label>
                      </div>
                    </th> */}
                    <th className="text-[12px] font-normal text-black-shade/40 border-r px-3">
                      <p className="w-[54px]">Method Name</p>
                    </th>
                    <th className="text-[12px] font-normal text-black-shade/40 border-r px-3">
                      <p className="w-[54px]">Method Shortname</p>
                    </th>
                    {/* <th className="text-[12px] font-normal text-black-shade/40  px-3">
                      isEnabled
                    </th> */}
                  </tr>
                </thead>
                <tbody className="">
                  {methods.map((method) => (
                    <tr
                      key={method.id}
                      className=" border-b-2 hover:bg-black-shade/5"
                    >
                      {/* <td className="p-3 text-black-shade/40 border-r">
                        <div>
                          <label class="container">
                            <img
                              src={t.image}
                              alt=""
                              className="w-[35px] h-[35px]"
                            />
                            <input type="checkbox" />
                            <span
                              class="checkmark"
                              style={{ marginTop: "10px" }}
                            ></span>
                          </label>
                        </div>
                      </td> */}
                      <td className="text-[12px] w-[54px] text-black-shade/40 border-r p-3">
                        <div>
                          <label class="container">
                            {method.name}
                            <input
                              type="checkbox"
                              checked={selectedMethods.includes(method.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMethods((prev) => [
                                    ...prev,
                                    method.name,
                                  ]);
                                } else {
                                  setSelectedMethods((prev) =>
                                    prev.filter((name) => name !== method.name)
                                  );
                                }
                              }}
                            />
                            <span
                              class="checkmark"
                              style={{ marginTop: "2px" }}
                            ></span>
                          </label>
                        </div>
                      </td>
                      <td className="text-[12px] w-[54px] text-black-shade/40 border-r p-3">
                        {method.code}
                      </td>
                      {/* <td className="text-[12px] text-black-shade/40 p-3">
                        {t.isEnabled}
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              className={`bg-white rounded-md w-1/2 shadow-md ${!selectedMethods.includes("pay with card")
                ? "opacity-50 pointer-events-none"
                : ""
                }`}
            >
              <p className="text-xl font-medium text-blue-grey/[87%] p-5">
                Setup Scheme
              </p>
              <div className="flex items-center justify-between border-2 py-4">
                <div className="flex items-center w-[50%] px-3 justify-between">
                  <span className="text-[12px] font-semibold text-blue-grey/[87%]">
                    SCHEMES
                  </span>
                </div>
                <span className=" border-x-2 px-3 text-[12px] font-normal text-black-shade">
                  {selectedSchemes.length} Selected
                </span>
                <div className="flex items-center px-3">
                  <span className="w-[30%] flex items-center gap-6 gap justify-between">
                    <span>
                      <svg
                        width="16"
                        height="15"
                        viewBox="0 0 16 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.5 12.0504V14.5834C0.5 14.8167 0.683308 15 0.916609 15H3.44959C3.55791 15 3.66623 14.9583 3.74122 14.875L12.84 5.78461L9.71539 2.66005L0.624983 11.7505C0.541661 11.8338 0.5 11.9338 0.5 12.0504ZM15.2563 3.36828C15.5812 3.04333 15.5812 2.5184 15.2563 2.19345L13.3066 0.243716C12.9816 -0.0812387 12.4567 -0.0812387 12.1317 0.243716L10.6069 1.7685L13.7315 4.89307L15.2563 3.36828Z"
                          fill="#1C1C1C"
                        />
                      </svg>
                    </span>
                    <span>
                      <svg
                        onClick={() => deleteScheme()}
                        className="cursor-pointer"
                        width="15"
                        height="17"
                        viewBox="0 0 15 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14.375 2.75H0.625C0.279822 2.75 0 3.02982 0 3.375C0 3.72018 0.279822 4 0.625 4H14.375C14.7202 4 15 3.72018 15 3.375C15 3.02982 14.7202 2.75 14.375 2.75Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M5 7.125V12.125C5 12.4702 5.27982 12.75 5.625 12.75C5.97018 12.75 6.25 12.4702 6.25 12.125V7.125C6.25 6.77982 5.97018 6.5 5.625 6.5C5.27982 6.5 5 6.77982 5 7.125Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M8.75 7.125V12.125C8.75 12.4702 9.02982 12.75 9.375 12.75C9.72018 12.75 10 12.4702 10 12.125V7.125C10 6.77982 9.72018 6.5 9.375 6.5C9.02982 6.5 8.75 6.77982 8.75 7.125Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M2.5 15.25V3.375C2.5 3.02982 2.22018 2.75 1.875 2.75C1.52982 2.75 1.25 3.02982 1.25 3.375V15.25C1.25 15.7678 1.61612 16.1339 1.61612 16.1339C1.98223 16.5 2.5 16.5 2.5 16.5H12.5C13.0178 16.5 13.3839 16.1339 13.3839 16.1339C13.75 15.7678 13.75 15.25 13.75 15.25V3.375C13.75 3.02982 13.4702 2.75 13.125 2.75C12.7798 2.75 12.5 3.02982 12.5 3.375V15.25H2.5Z"
                          fill="#1C1C1C"
                        />
                        <path
                          d="M4.29918 0.799175C3.75 1.34835 3.75 2.125 3.75 2.125V3.375C3.75 3.72018 4.02982 4 4.375 4C4.72018 4 5 3.72018 5 3.375V2.125C5 1.86612 5.18306 1.68306 5.18306 1.68306C5.36612 1.5 5.625 1.5 5.625 1.5H9.375C9.63388 1.5 9.81694 1.68306 9.81694 1.68306C10 1.86612 10 2.125 10 2.125V3.375C10 3.72018 10.2798 4 10.625 4C10.9702 4 11.25 3.72018 11.25 3.375V2.125C11.25 1.34835 10.7008 0.799175 10.7008 0.799175C10.1517 0.25 9.375 0.25 9.375 0.25H5.625C4.84835 0.25 4.29918 0.799175 4.29918 0.799175Z"
                          fill="#1C1C1C"
                        />
                      </svg>
                    </span>
                    <span>
                      <FaRegSave
                        className="cursor-pointer"
                        onClick={() => postPaymentScheme()}
                      />
                    </span>
                  </span>
                </div>
              </div>
              <table className="w-[100%]">
                <thead>
                  <tr className="text-left border-b-2">
                    <th className="text-[12px] font-normal text-black-shade/40 border-r p-3">
                      <div className="px-9">
                        Logo
                        {/* <label
                          class="container"
                          style={{
                            fontWeight: "normal",
                          }}
                        >
                          Logo
                          <input type="checkbox" />
                          <span class="checkmark"></span>
                        </label> */}
                      </div>
                    </th>
                    <th className="text-[12px] font-normal text-black-shade/40 border-r px-3">
                      <p className="w-[54px]">Scheme Name</p>
                    </th>
                    <th className="text-[12px] font-normal text-black-shade/40 border-r px-3">
                      <p className="w-[54px]">Scheme Shortname</p>
                    </th>
                    {/* <th className="text-[12px] font-normal text-black-shade/40  px-3">
                      isEnabled
                    </th> */}
                  </tr>
                </thead>
                <tbody className="">
                  {schemes.map((scheme) => {
                    const matched = schemeImages.find(
                      (img) =>
                        img.name.toLowerCase() === scheme.name.toLowerCase()
                    );
                    return (
                      <tr
                        key={scheme.id}
                        className=" border-b-2 hover:bg-black-shade/5"
                      >
                        <td className="p-3 text-black-shade/40 border-r">
                          <div>
                            <label class="container">
                              <img
                                src={matched?.image}
                                alt=""
                                className="w-[35px] h-[35px]"
                              />
                              <input
                                type="checkbox"
                                checked={selectedSchemes.includes(scheme.name)}
                                disabled={
                                  !selectedMethods.includes("pay with card")
                                }
                                onChange={(e) => {
                                  if (
                                    !selectedMethods.includes("pay with card")
                                  )
                                    return;

                                  if (e.target.checked) {
                                    setSelectedSchemes((prev) => [
                                      ...prev,
                                      scheme.name,
                                    ]);
                                  } else {
                                    setSelectedSchemes((prev) =>
                                      prev.filter(
                                        (name) => name !== scheme.name
                                      )
                                    );
                                  }
                                }}
                              />
                              <span
                                class="checkmark"
                                style={{ marginTop: "10px" }}
                              ></span>
                            </label>
                          </div>
                        </td>
                        <td className="text-[12px] w-[54px] text-black-shade/40 border-r p-3">
                          {scheme.name}
                        </td>
                        <td className="text-[12px] w-[54px] text-black-shade/40 border-r p-3">
                          {scheme.code}
                        </td>
                        {/* <td className="text-[12px] text-black-shade/40 p-3">
                        {t.isEnabled}
                      </td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white rounded-md mt-4">
            <div className="flex items-center justify-end gap-3 px-11">
              <p className="border-r pr-5 font-normal text-[12px]">
                1 Selected
              </p>
              <span>
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_3367_2872)">
                    <path
                      d="M20.3333 14.3333V18.5C20.3333 18.9583 19.9583 19.3333 19.5 19.3333H9.5C9.04167 19.3333 8.66667 18.9583 8.66667 18.5V14.3333C8.66667 13.875 8.29167 13.5 7.83333 13.5C7.375 13.5 7 13.875 7 14.3333V19.3333C7 20.25 7.75 21 8.66667 21H20.3333C21.25 21 22 20.25 22 19.3333V14.3333C22 13.875 21.625 13.5 21.1667 13.5C20.7083 13.5 20.3333 13.875 20.3333 14.3333ZM15.3333 14.0583L16.9 12.4917C17.225 12.1667 17.75 12.1667 18.075 12.4917C18.4 12.8167 18.4 13.3417 18.075 13.6667L15.0833 16.6583C14.7583 16.9833 14.2333 16.9833 13.9083 16.6583L10.9167 13.6667C10.5917 13.3417 10.5917 12.8167 10.9167 12.4917C11.2417 12.1667 11.7667 12.1667 12.0917 12.4917L13.6667 14.0583V6.83333C13.6667 6.375 14.0417 6 14.5 6C14.9583 6 15.3333 6.375 15.3333 6.83333V14.0583Z"
                      fill="#323232"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3367_2872">
                      <rect width="28" height="28" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              <span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 8.75C5.69036 8.75 6.25 9.30964 6.25 10C6.25 10.6904 5.69036 11.25 5 11.25C4.30964 11.25 3.75 10.6904 3.75 10C3.75 9.30964 4.30964 8.75 5 8.75Z"
                    fill="#1C1C1C"
                  />
                  <path
                    d="M10 8.75C10.6904 8.75 11.25 9.30964 11.25 10C11.25 10.6904 10.6904 11.25 10 11.25C9.30964 11.25 8.75 10.6904 8.75 10C8.75 9.30964 9.30964 8.75 10 8.75Z"
                    fill="#1C1C1C"
                  />
                  <path
                    d="M16.25 10C16.25 9.30964 15.6904 8.75 15 8.75C14.3096 8.75 13.75 9.30964 13.75 10C13.75 10.6904 14.3096 11.25 15 11.25C15.6904 11.25 16.25 10.6904 16.25 10Z"
                    fill="#1C1C1C"
                  />
                </svg>
              </span>
            </div>
            <div className="flex items-center justify-between px-3 gap-2">
              <div className="flex items-center gap-2">
                <p className="border-r pr-5 text-sm font-semibold">
                  Transaction History
                </p>
                <div className="flex items-center gap-2 border-r pr-5">
                  <p className="text-[12px] font-normal">All Transaction</p>
                  <span>
                    <svg
                      width="12"
                      height="6"
                      viewBox="0 0 12 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.5 0.75L6 5.25L10.5 0.75"
                        stroke="#464255"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-black w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-[12px] font-normal">Approved</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-black w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-[12px] font-normal">Failed</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-black w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-[12px] font-normal">Pending</p>
                </div>
              </div>
              <Pagination
                totalItems={merchantDetails.merchantTransactions?.length}
                itemsPerPage={itemsPerPage}
                paginate={paginate}
                currentPage={currentPage}
              />
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left border-b-2">
                  <th className="px-3 w-[68px] border-r">
                    <div className="">
                      <label
                        class="container"
                        style={{
                          fontWeight: "normal",
                          wordWrap: "break-word",
                        }}
                      >
                        Transaction ID
                        <input type="checkbox" />
                        <span
                          class="checkmark"
                          style={{ marginTop: "10px" }}
                        ></span>
                      </label>
                    </div>
                  </th>
                  <th className="border-r font-normal px-3 py-3 text-[12px] text-black-shade/40">
                    <p className="w-[54px]">Transaction Description</p>
                  </th>

                  <th className="px-3 py-3 font-normal text-[12px] border-r text-black-shade/40">
                    <p className="w-[54px]">Currency</p>
                  </th>
                  <th className="px-3 py-3 font-normal text-[12px] border-r text-black-shade/40">
                    <p className="w-[54px]">Masked Pan</p>
                  </th>
                  <th className="px-3 py-3 font-normal text-[12px] border-r text-black-shade/40">
                    <p className="w-[54px]">Transaction Amount</p>
                  </th>

                  <th className="px-3 border-r font-normal text-[12px] text-black-shade/40">
                    <p className="w-[54px]">Transaction Date</p>
                  </th>
                  <th className="text-black-shade/40 text-[12px] px-3 font-normal">
                    Status
                  </th>
                  <th className="text-black-shade/40 text-[12px] px-3 font-normal">
                    Scheme
                  </th>
                </tr>
              </thead>
              <tbody className="cursor-pointer relative">
                {currentItems?.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="bg-white border-b text-[12px] text-black-shade font-normal hover:bg-black-shade/5"
                  >
                    <td className="py-2 px-3">
                      <label class="container" style={{ color: "#1C1C1C" }}>
                        {transaction.id}
                        <input type="checkbox" />
                        <span class="checkmark"></span>
                      </label>
                    </td>
                    <td className="py-2 px-3 w-[90px]">
                      <div className="text-wrap">{transaction.description}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div>{transaction.currency}</div>
                    </td>

                    {/* <span className="flex items-center gap-6 justify-around"> */}

                    <td className="py-2 px-3">
                      <div className="">{transaction.pan}</div>
                    </td>
                    <td className="py-2 px-3">
                      <div>{transaction.amount}</div>
                    </td>

                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M10.5 2V1.5C10.5 1.22386 10.7239 1 11 1C11.2761 1 11.5 1.22386 11.5 1.5V2H13C13 2 13.4142 2 13.7071 2.29289C13.7071 2.29289 14 2.58579 14 3V13C14 13 14 13.4142 13.7071 13.7071C13.7071 13.7071 13.4142 14 13 14H3C3 14 2.58579 14 2.29289 13.7071C2.29289 13.7071 2 13.4142 2 13V3C2 3 2 2.58579 2.29289 2.29289C2.29289 2.29289 2.58579 2 3 2H4.5V1.5C4.5 1.22386 4.72386 1 5 1C5.27614 1 5.5 1.22386 5.5 1.5V2H10.5ZM3 6V13H13V6H3ZM13 5H3V3H4.5V3.5C4.5 3.77614 4.72386 4 5 4C5.27614 4 5.5 3.77614 5.5 3.5V3H10.5V3.5C10.5 3.77614 10.7239 4 11 4C11.2761 4 11.5 3.77614 11.5 3.5V3H13V5Z"
                              fill="#1C1C1C"
                              fill-opacity="0.4"
                            />
                          </svg>
                        </span>
                        <span className="">
                          {new Date(transaction.initiated).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-3">
                      <div className="bg-green-400 rounded-full w-[6px] h-[6px]"></div>
                    </td>
                    <td className="py-2 px-3">
                      <div>{transaction.scheme}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>
        {`
              /* Customize the label (the container) */
.container {
  display: block;
  position: relative;
  padding-left: 35px;
  font-size:12px;
  cursor: pointer;
  color: rgba(28, 28, 28, 0.4);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 13.5px;
  width: 13.5px;
  background-color: #eee;
  border-radius: 3px;
  margin-top:2px;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: #ccc;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: #323232;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 6px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

/* ---------- MSC Modal CSS ---------- */
:root {
  --payloft-blue: #1e9ee0;
  --payloft-orange: #f5821f;
  --ink: #1b2430;
  --ink-soft: #5b6472;
  --border: #e6e9ee;
  --bg: #f7f8fa;
  --white: #ffffff;
  --danger: #e5484d;
  --success: #1fa971;
  --radius: 10px;
  --shadow: 0 10px 40px rgba(20,30,50,.16);
}

.msc-btn {
  border: none;
  border-radius: 8px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform .12s ease, box-shadow .12s ease, background .15s ease;
}
.msc-btn:active {
  transform: translateY(1px);
}
.msc-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}
.msc-btn-primary {
  background: var(--payloft-orange);
  color: #fff;
}
.msc-btn-primary:hover {
  background: #e0731a;
}

.msc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15,22,32,.55);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}
.msc-overlay.msc-open {
  display: flex;
}
.msc-modal {
  background: var(--white);
  border-radius: 14px;
  box-shadow: var(--shadow);
  width: 100%;
  max-width: 520px;
  max-height: 88vh;
  overflow: auto;
  animation: msc-pop .18s ease;
  text-align: left;
}
@keyframes msc-pop {
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: none; }
}

.msc-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 22px 14px;
  border-bottom: 1px solid var(--border);
}
.msc-modal-head .titles .eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: var(--payloft-blue);
  margin-bottom: 4px;
}
.msc-modal-head h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 800;
  color: var(--ink);
}
.msc-modal-head .sub {
  margin-top: 4px;
  font-size: 13px;
  color: var(--ink-soft);
}
.msc-close-x {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: var(--ink-soft);
  padding: 4px 6px;
  border-radius: 6px;
}
.msc-close-x:hover {
  background: var(--bg);
  color: var(--ink);
}

.msc-modal-body {
  padding: 20px 22px;
}
.msc-modal-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 22px;
  border-top: 1px solid var(--border);
  background: #fbfcfd;
  border-radius: 0 0 14px 14px;
}

.msc-steps {
  display: flex;
  gap: 6px;
  padding: 0 22px;
  margin-top: 12px;
}
.msc-steps span {
  height: 4px;
  flex: 1;
  border-radius: 2px;
  background: var(--border);
}
.msc-steps span.done {
  background: var(--payloft-blue);
}

.msc-payer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.msc-payer-card {
  border: 2px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  text-align: left;
  background: #fff;
  transition: border-color .12s ease, background .12s ease;
}
.msc-payer-card:hover {
  border-color: #c9d3de;
}
.msc-payer-card.selected {
  border-color: var(--payloft-blue);
  background: #f0f9ff;
}
.msc-payer-card .icon {
  font-size: 22px;
  margin-bottom: 8px;
  display: block;
}
.msc-payer-card .name {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
  color: var(--ink);
}
.msc-payer-card .desc {
  font-size: 12.5px;
  color: var(--ink-soft);
  line-height: 1.4;
}

.msc-field {
  margin-bottom: 16px;
}
.msc-field label {
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-soft);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.msc-field .hint {
  font-size: 12px;
  color: var(--ink-soft);
  margin-top: 5px;
}
.msc-row2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.msc-input, .msc-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--ink);
  background: #fff;
}
.msc-input:focus, .msc-select:focus {
  outline: none;
  border-color: var(--payloft-blue);
  box-shadow: 0 0 0 3px rgba(30,158,224,.15);
}
.msc-input:disabled {
  background: #f2f4f6;
  color: #9aa4b1;
}

.msc-seg {
  display: flex;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.msc-seg button {
  flex: 1;
  border: none;
  background: #fff;
  padding: 9px 10px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink-soft);
  cursor: pointer;
  border-right: 1px solid var(--border);
}
.msc-seg button:last-child {
  border-right: none;
}
.msc-seg button.active {
  background: var(--payloft-blue);
  color: #fff;
}

.msc-amount-wrap {
  position: relative;
}
.msc-amount-wrap .suffix {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: var(--ink-soft);
  font-weight: 600;
  pointer-events: none;
}

.msc-checkline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--ink-soft);
  margin-top: 8px;
  cursor: pointer;
}
.msc-checkline input {
  width: auto;
  margin: 0;
}

.msc-banner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: left;
}
.msc-banner.info { background: #eef7fe; color: #0d6ba8; border: 1px solid #cfe9f8; }
.msc-banner.warn { background: #fff7ea; color: #95650b; border: 1px solid #f6e3bb; }
.msc-banner.success { background: #eafaf3; color: #0d7a52; border: 1px solid #bfeeda; }
.msc-banner.error { background: #fdeeee; color: #b3282d; border: 1px solid #f5c8c9; }

.msc-preview {
  border: 1px dashed var(--border);
  border-radius: 8px;
  padding: 12px 14px;
  background: #fbfcfd;
  font-size: 13px;
  color: var(--ink-soft);
  text-align: left;
}
.msc-preview b {
  color: var(--ink);
}

.msc-err-text {
  color: var(--danger);
  font-size: 12px;
  margin-top: 5px;
}
.msc-field.invalid input, .msc-field.invalid select {
  border-color: var(--danger);
}

.msc-spinner {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  animation: msc-spin .7s linear infinite;
  display: inline-block;
}
@keyframes msc-spin {
  to { transform: rotate(360deg); }
}

.msc-back-link {
  background: none;
  border: none;
  color: var(--payloft-blue);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.msc-back-link:hover {
  text-decoration: underline;
}
        `}
      </style>
      {editModal && (
        <EditMerchant
          merchantInfo={merchantDetails}
          setEditModal={setEditModal}
          merId={id}
        />
      )}
      {editTxpgModal && (
        <EditTxpg
          setEditTxpgModal={setEditTxpgModal}
          hasTxpg={hasTxpg}
          id={id}
          txpgInfo={txpgDetails}
        />
      )}

      {showMscModal && (
        <div className="msc-overlay msc-open" onClick={(e) => { if (e.target.className && typeof e.target.className === 'string' && e.target.className.includes("msc-overlay")) { setShowMscModal(false); } }}>
          <div className="msc-modal" role="dialog" aria-modal="true" aria-labelledby="mscTitle">

            <div className="msc-modal-head">
              <div className="titles">
                <div className="eyebrow">Merchant Service Charge · {merchantDetails?.name} #{id}</div>
                <h2 id="mscTitle">
                  {mscStep === 1 ? "Who bears the service charge?" : "Set up the fee for this merchant"}
                </h2>
                <div className="sub">
                  {mscStep === 1
                    ? "This decides how the fee configuration form is set up."
                    : "The customer will see this fee added at checkout. Fill this in exactly as agreed with the merchant."}
                </div>
              </div>
              <button className="msc-close-x" onClick={() => setShowMscModal(false)} aria-label="Close">✕</button>
            </div>

            <div className="msc-steps">
              <span className="done"></span>
              <span className={mscStep === 2 ? "done" : ""}></span>
            </div>

            {/* STEP 1: payer choice */}
            {mscStep === 1 && (
              <div className="msc-modal-body">
                {mscErrorMsg && <div className="msc-banner error">{mscErrorMsg}</div>}
                {mscSuccessMsg && <div className="msc-banner success">{mscSuccessMsg}</div>}

                <div className="msc-payer-grid">
                  <button
                    type="button"
                    className={`msc-payer-card ${payer === 'merchant' ? 'selected' : ''}`}
                    onClick={() => setPayer('merchant')}
                  >
                    <span className="icon">🏪</span>
                    <div className="name">Merchant absorbs it</div>
                    <div className="desc">The MSC is deducted from the merchant's settlement. The customer pays the exact checkout amount.</div>
                  </button>
                  <button
                    type="button"
                    className={`msc-payer-card ${payer === 'customer' ? 'selected' : ''}`}
                    onClick={() => setPayer('customer')}
                  >
                    <span className="icon">🧾</span>
                    <div className="name">Customer pays it</div>
                    <div className="desc">The MSC is added on top at checkout. You'll set the fee structure on the merchant's behalf.</div>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: fee configuration form (only shown when customer pays) */}
            {mscStep === 2 && (
              <div className="msc-modal-body">
                <button className="msc-back-link" onClick={() => setMscStep(1)}>← Back</button>
                <div style={{ height: "12px" }}></div>

                {mscErrorMsg && <div className="msc-banner error">{mscErrorMsg}</div>}
                {mscSuccessMsg && <div className="msc-banner success">{mscSuccessMsg}</div>}

                <form onSubmit={handleMscSubmit} noValidate>
                  <div className="msc-field">
                    <label htmlFor="currencyCode">Currency</label>
                    <select
                      id="currencyCode"
                      className="msc-select"
                      value={currencyCode}
                      onChange={(e) => setCurrencyCode(e.target.value)}
                    >
                      <option value="NGN">NGN — Naira</option>
                      <option value="USD">USD — US Dollar</option>
                      <option value="GBP">GBP — Pound Sterling</option>
                    </select>
                  </div>

                  <div className="msc-field">
                    <label htmlFor="scheme">Scheme / Payment Method</label>
                    <select
                      id="scheme"
                      className="msc-select"
                      value={scheme}
                      onChange={(e) => setScheme(e.target.value)}
                    >
                      <option value="card">card</option>
                      <option value="payattitude">payattitude</option>
                      <option value="ussd">ussd</option>
                      <option value="transfer">transfer</option>
                    </select>
                  </div>

                  <div className="msc-field">
                    <label>Fee type</label>
                    <div className="msc-seg">
                      <button
                        type="button"
                        className={feeType === 'Percentage' ? 'active' : ''}
                        onClick={() => setFeeType('Percentage')}
                      >
                        Percentage
                      </button>
                      <button
                        type="button"
                        className={feeType === 'Flat' ? 'active' : ''}
                        onClick={() => setFeeType('Flat')}
                      >
                        Flat amount
                      </button>
                    </div>
                  </div>

                  <div className="msc-row2">
                    <div className="msc-field">
                      <label htmlFor="feeValue">Fee value</label>
                      <div className="msc-amount-wrap">
                        <input
                          type="number"
                          id="feeValue"
                          min="0"
                          step="0.01"
                          className="msc-input"
                          placeholder={feeType === 'Percentage' ? 'e.g. 1.5' : 'e.g. 100'}
                          value={feeValue}
                          onChange={(e) => setFeeValue(e.target.value)}
                        />
                        <span className="suffix">{feeType === 'Percentage' ? '%' : currencyCode}</span>
                      </div>
                    </div>

                    <div className="msc-field">
                      <label htmlFor="maxFeeCap">Max fee cap <span style={{ textTransform: "none", fontWeight: "500" }}>(optional)</span></label>
                      <div className="msc-amount-wrap">
                        <input
                          type="number"
                          id="maxFeeCap"
                          min="0"
                          step="0.01"
                          className="msc-input"
                          placeholder="No cap"
                          disabled={noCap}
                          value={maxFeeCap}
                          onChange={(e) => setMaxFeeCap(e.target.value)}
                        />
                        <span className="suffix">{currencyCode}</span>
                      </div>
                      <label className="msc-checkline">
                        <input
                          type="checkbox"
                          id="noCap"
                          checked={noCap}
                          onChange={(e) => {
                            setNoCap(e.target.checked);
                            if (e.target.checked) setMaxFeeCap("");
                          }}
                        />
                        Use the engine's default cap
                      </label>
                    </div>
                  </div>

                  {feeValue && !isNaN(feeValue) && (
                    <div className="msc-preview">
                      On a transaction, the customer pays an extra <b>{feeType === 'Percentage' ? `${feeValue}%` : `${currencyCode} ${Number(feeValue).toLocaleString()}`}</b>, capped at <b>{noCap ? 'engine default' : (maxFeeCap ? `${currencyCode} ${Number(maxFeeCap).toLocaleString()}` : 'engine default')}</b>.
                    </div>
                  )}
                </form>
              </div>
            )}

            {mscStep === 1 && (
              <div className="msc-modal-foot">
                <span style={{ fontSize: "12.5px", color: "var(--ink-soft)", width: '200px' }}>
                  You can change this later before it's approved.
                </span>
                <button
                  className="msc-btn msc-btn-primary"
                  disabled={!payer || submittingMsc}
                  onClick={() => {
                    if (payer === 'customer') {
                      setMscStep(2);
                    } else {
                      handleMscSubmit();
                    }
                  }}
                >
                  {submittingMsc ? (
                    <>
                      <span className="msc-spinner"></span> Submitting...
                    </>
                  ) : payer === 'customer' ? (
                    "Continue to fee setup"
                  ) : (
                    "Confirm merchant absorbs MSC"
                  )}
                </button>
              </div>
            )}

            {mscStep === 2 && (
              <div className="msc-modal-foot">
                <span style={{ fontSize: "12.5px", color: "var(--ink-soft)" }}>
                  Goes to the Support Maker for approval next.
                </span>
                <button
                  className="msc-btn msc-btn-primary"
                  disabled={submittingMsc}
                  onClick={handleMscSubmit}
                >
                  {submittingMsc ? (
                    <>
                      <span className="msc-spinner"></span> Submitting...
                    </>
                  ) : (
                    "Submit for approval"
                  )}
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantInfo;
